import { useCallback } from 'react';

import { useCanUserConnectToBlackDiamond } from '@/modules/assetProviderIntegrations/blackDiamond/hooks/useIsBlackDiamondEnabled';
import { ASSET_INTEGRATION_PROVIDER_DISPLAY_NAMES } from '@/modules/assetProviderIntegrations/shared/constants';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { IntegrationProvider } from '@/types/schema';
import { getPublicImageUrl } from '@/utils/staticFiles';

import { IntegrationRow } from '../IntegrationRow';

interface BlackDiamondCardProps {
  userAccountLinked: boolean;
  externalUserName?: string | null;
}

export function BlackDiamondCard({
  userAccountLinked,
  externalUserName,
}: BlackDiamondCardProps) {
  const canEditHouseholdData = useCanEditHouseholdData();
  const canUserConnectToBD = useCanUserConnectToBlackDiamond();
  const { assetIntegrationsEnabled } = useTenantDetailsContext();
  const currentUser = useCurrentUser();
  const isExpired =
    currentUser?.userExpiredIntegrationNames?.includes(
      ASSET_INTEGRATION_PROVIDER_DISPLAY_NAMES.BLACK_DIAMOND
    ) ?? false;

  const getBlackDiamondButtonProps = useCallback(() => {
    if (
      !canEditHouseholdData ||
      !assetIntegrationsEnabled ||
      !canUserConnectToBD
    ) {
      return {
        disabled: true,
        href: '#',
        children: 'Link account',
      };
    }

    const currentPageURL = getCompletePathFromRouteKey('USER_SETTINGS', {});
    if (userAccountLinked) {
      return {
        href: `/api/v1/blackdiamond/auth/logout?redirect=${currentPageURL}`,
        children: 'Unlink',
        method: 'post' as const,
      };
    } else {
      return {
        href: `/api/v1/blackdiamond/auth/initiate?redirect=${currentPageURL}`,
        children: 'Link account',
      };
    }
  }, [
    canEditHouseholdData,
    assetIntegrationsEnabled,
    canUserConnectToBD,
    userAccountLinked,
  ]);

  return (
    <IntegrationRow
      integrationEnabled={userAccountLinked}
      isExpired={isExpired}
      externalUserName={externalUserName}
      description={
        !canUserConnectToBD ? 'Contact your administrator' : undefined
      }
      logo={
        <img
          alt="Black Diamond"
          height={24}
          src={getPublicImageUrl('/blackDiamondLogo.png')}
        />
      }
      buttonProps={getBlackDiamondButtonProps()}
      integrationProvider={IntegrationProvider.BlackDiamond}
    />
  );
}
